import { Tag } from 'antd'
import classNames from 'classnames'

import AtomButton from '@/components/Atoms/AtomButton/AtomButton'
import AtomNotice from '@/components/Atoms/AtomNotice/AtomNotice'
import type { OptionType } from '@/types'

import { IMPORT_FILE_FORMAT_LABELS } from '../../lib/stockImportsFileFormats'
import { formatFileSize } from '../../lib/stockImportsFormat'
import { labelWithTooltip } from '../../RequiredLabel/labelWithTooltip'
import type {
  ImportFileFormat,
  ValidationResponse,
} from '../../types/stockImports.types'
import {
  type ConfigFieldContext,
  ConfigSelectField,
} from '../ConfigFields/ConfigFields'
import cn from '../ConfigModal.module.scss'
import type { ConfigFileFormat, ConfigSourceType } from '../lib/configFormModel'
import {
  CONFIG_TOOLTIPS,
  FILE_FORMAT_OPTIONS,
  SOURCE_NOTICES,
} from '../lib/configModalTexts'
import ConfigConnectionFields from './ConfigConnectionFields'

type SourceValidationView = {
  label: string
  loading: boolean
  result: ValidationResponse | null
  onValidate: () => void
}

type ConfigSourceStepProps = {
  context: ConfigFieldContext
  hidden: boolean
  isSelfSupplierMode: boolean
  supplierId: string
  sourceType: ConfigSourceType
  fileFormat: ConfigFileFormat
  supplierOptions: OptionType[]
  storeOptions: OptionType[]
  sourceTypeOptions: OptionType[]
  storesLoading: boolean
  storesWithConfig: Set<string>
  mismatchedStoreId: string | null
  mismatchedStoreName: string | null
  validation: SourceValidationView | null
  detectedFileFormat: ImportFileFormat | null
  onSupplierChange: () => void
  onSourceTypeChange: (sourceType: string) => void
  onFileFormatChange: (fileFormat: string) => void
  onFileMaskBlur: () => void
}

function ConfigSourceStep({
  context,
  hidden,
  isSelfSupplierMode,
  supplierId,
  sourceType,
  fileFormat,
  supplierOptions,
  storeOptions,
  sourceTypeOptions,
  storesLoading,
  storesWithConfig,
  mismatchedStoreId,
  mismatchedStoreName,
  validation,
  detectedFileFormat,
  onSupplierChange,
  onSourceTypeChange,
  onFileFormatChange,
  onFileMaskBlur,
}: ConfigSourceStepProps) {
  const sourceNotice = SOURCE_NOTICES[sourceType]
  const validationResult = validation?.result

  return (
    <div
      className={classNames(cn.step, hidden && cn.stepHidden)}
      aria-hidden={hidden}
    >
      <div className={cn.grid}>
        {!isSelfSupplierMode && (
          <ConfigSelectField
            context={context}
            name="supplierId"
            title="Поставщик"
            required
            placeholder="Выберите поставщика"
            options={supplierOptions}
            onChange={onSupplierChange}
          />
        )}
        {supplierId && (
          <ConfigSelectField
            context={context}
            name="storeId"
            title={labelWithTooltip('Склад', CONFIG_TOOLTIPS.store)}
            required
            placeholder={
              storesLoading ? 'Загружаем склады...' : 'Выберите склад'
            }
            options={storeOptions}
            loading={storesLoading}
            disabled={storesLoading || storeOptions.length === 0}
            notFoundContent="У поставщика нет складов"
            optionRender={(option) => (
              <span className={cn.storeOption}>
                {option.label}
                {option.value === mismatchedStoreId ? (
                  <Tag color="error">Передан другому поставщику</Tag>
                ) : (
                  typeof option.value === 'string' &&
                  storesWithConfig.has(option.value) && (
                    <Tag color="warning">Уже есть настройка загрузки</Tag>
                  )
                )}
              </span>
            )}
          />
        )}
        <ConfigSelectField
          context={context}
          name="sourceType"
          title={labelWithTooltip('Источник', CONFIG_TOOLTIPS.sourceType)}
          required
          options={sourceTypeOptions}
          onChange={onSourceTypeChange}
        />
        <ConfigSelectField
          context={context}
          name="fileFormat"
          title={labelWithTooltip('Формат файла', CONFIG_TOOLTIPS.fileFormat)}
          required
          options={FILE_FORMAT_OPTIONS}
          onChange={onFileFormatChange}
        />
      </div>

      {mismatchedStoreName && (
        <AtomNotice type="warning">
          Склад «{mismatchedStoreName}» передан другому поставщику — выберите
          другой склад.
        </AtomNotice>
      )}

      {sourceType && (
        <>
          <p className={cn.sectionTitle}>Подключение</p>
          {sourceNotice && <AtomNotice>{sourceNotice}</AtomNotice>}
          <ConfigConnectionFields
            context={context}
            sourceType={sourceType}
            fileFormat={fileFormat}
            onFileMaskBlur={onFileMaskBlur}
          />
        </>
      )}

      {validation && (
        <div className={cn.actionsRow}>
          <AtomButton
            variant="secondary"
            loading={validation.loading}
            onClick={validation.onValidate}
          >
            {validation.label}
          </AtomButton>
        </div>
      )}
      {validationResult && (
        <AtomNotice
          type={validationResult.success ? 'success' : 'error'}
          description={
            sourceType === 'URL' && validationResult.success
              ? `Файл: ${validationResult.fileName ?? '—'} · Формат: ${validationResult.fileFormat ?? '—'} · Размер: ${formatFileSize(validationResult.fileSize)}`
              : undefined
          }
        >
          {validationResult.message}
        </AtomNotice>
      )}
      {detectedFileFormat && (
        <AtomNotice
          type="warning"
          action={
            <AtomButton
              variant="secondary"
              onClick={() => onFileFormatChange(detectedFileFormat)}
            >
              Выбрать {IMPORT_FILE_FORMAT_LABELS[detectedFileFormat]}
            </AtomButton>
          }
        >
          По ссылке лежит файл {validationResult?.fileFormat}, а в поле «Формат
          файла» выбран {fileFormat && IMPORT_FILE_FORMAT_LABELS[fileFormat]}
        </AtomNotice>
      )}
    </div>
  )
}

export default ConfigSourceStep
